import { Dropdown } from 'antd'
import classNames from 'classnames'
import { type FC, useState } from 'react'

import { getScheduleStaffColor } from 'features/Home/Book/Booking/Schedule/PatientSchedule/ScheduleCalendar/scheduleInstance'

import { ProviderMenu } from '../../ProviderMenu'
import { Avatar, PROVIDER_TEXT, type PersonOption } from '../../providerShared'
import {
  makePrimary,
  MAX_VISIT_PEOPLE,
  peopleOf,
  type VisitPeople,
  withPerson,
  withoutPerson,
} from '../seriesPeople'
import { SERIES_TEXT_V2 } from '../seriesText'
import styles from './seriesGrid.module.scss'

const MAX_AVATARS = 3

export type ProviderCellProps = {
  // The row's 1-based position; names the menu "Visit N providers".
  position: number
  options: PersonOption[]
  value: VisitPeople | null
  unknown?: { names: string[]; primary: boolean }
  readOnly?: boolean
  lockedId?: number | null
  disabled?: boolean
  text?: string
  tone?: 'normal' | 'refused'
  onChange: (next: VisitPeople) => void
}

const personOf = (options: PersonOption[], id: number): PersonOption =>
  options.find((option) => option.id === id) ?? {
    id,
    name: `Provider #${id}`,
    role: '',
    color: getScheduleStaffColor(id),
  }

export const ProviderCell: FC<ProviderCellProps> = ({
  position,
  options,
  value,
  unknown,
  readOnly,
  lockedId = null,
  disabled,
  text,
  tone = 'normal',
  onChange,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const title = SERIES_TEXT_V2.rowProviders(position)
  const known = value?.people ?? []
  const unknownNames = unknown?.names ?? []
  const ordered = value
    ? [value.primaryId, ...value.people.filter((id) => id !== value.primaryId)]
    : []
  const primaryName =
    unknown?.primary && unknownNames.length
      ? unknownNames[0]
      : value
      ? personOf(options, value.primaryId).name
      : unknownNames[0] ?? ''
  const extra = Math.max(0, known.length + unknownNames.length - 1)
  const knownNames = ordered.map((id) => personOf(options, id).name)
  // Every person by name, primary first; the avatars show at most three.
  const allNames = unknown?.primary
    ? [...unknownNames.slice(0, 1), ...knownNames, ...unknownNames.slice(1)]
    : [...knownNames, ...unknownNames]
  const names = allNames.length ? allNames.join(', ') : undefined
  const label = primaryName
    ? `${title}: ${SERIES_TEXT_V2.providerSummary(primaryName, extra)}`
    : title

  // A save disables the cell; it must not pop back open when the save ends.
  if (disabled && isOpen) setIsOpen(false)

  const nextPeople = (id: number): VisitPeople | null => {
    if (!value) return peopleOf([id])
    if (!value.people.includes(id)) return withPerson(value, id)
    return withoutPerson(value, id)
  }
  const toggle = (id: number) => {
    const next = nextPeople(id)
    if (next) onChange(next)
  }

  const atCap = known.length >= MAX_VISIT_PEOPLE
  const soleKnown = known.length === 1 ? known : []
  const disabledIds = [
    ...(lockedId === null ? [] : [lockedId]),
    ...soleKnown,
    ...(atCap ? options.filter((o) => !known.includes(o.id)).map((o) => o.id) : []),
  ]

  const content = (
    <>
      <span className={styles.avatars}>
        {ordered.slice(0, MAX_AVATARS).map((id, index) => (
          <span
            key={id}
            className={classNames({ [styles.primaryRing]: index === 0 && !unknown?.primary })}>
            <Avatar person={personOf(options, id)} />
          </span>
        ))}
      </span>
      <span
        className={classNames(styles.boxText, {
          [styles.refusedText]: tone === 'refused',
        })}>
        {primaryName || text || ''}
      </span>
      {extra > 0 && <span className={styles.more}>{`+${extra}`}</span>}
    </>
  )

  if (readOnly) {
    return (
      <div className={classNames(styles.box, styles.providerCell)} title={names}>
        {content}
        {primaryName && text ? (
          <span
            className={classNames(styles.boxText, {
              [styles.refusedText]: tone === 'refused',
            })}>
            {text}
          </span>
        ) : null}
      </div>
    )
  }

  const menu = (
    <ProviderMenu
      ariaLabel={title}
      title={PROVIDER_TEXT.menuTitle}
      options={options}
      people={known}
      multiselect
      disabledIds={disabledIds}
      primaryId={value && known.length > 1 ? value.primaryId : null}
      onToggle={toggle}
      onMakePrimary={(id) => value && onChange(makePrimary(value, id))}
      onDone={() => setIsOpen(false)}
    />
  )

  return (
    <Dropdown
      open={isOpen}
      trigger={['click']}
      placement='bottomLeft'
      align={{ offset: [0, 4] }}
      popupRender={() => menu}
      onOpenChange={(next) => setIsOpen(next && !disabled)}>
      <button
        type='button'
        className={classNames(styles.box, styles.providerCell, styles.providerButton)}
        title={names}
        aria-label={label}
        aria-haspopup='listbox'
        aria-expanded={isOpen}
        disabled={disabled}>
        {content}
        <span className={styles.chev} aria-hidden='true'>
          ▾
        </span>
      </button>
    </Dropdown>
  )
}
